import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { fetchTradeHistory, keys } from "@/lib/queries";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { Badge } from "@/components/ui/Badge";
import { Select } from "@/components/ui/Select";
import { Pagination } from "@/components/ui/Pagination";
import { SortableHeader } from "@/components/ui/SortableHeader";
import { useTableState } from "@/lib/useTableState";
import { fmtDate, fmtQty, fmtRelative, statusLabel } from "@/lib/format";
import { CurrencyAmount } from "@/components/ui/CurrencyAmount";
import {
  ActivityScopeBar,
  ResetScopeButton,
  useActivityScope,
  useEventMarketScope,
  validateActivityScopeSearch,
  EMPTY_SCOPE,
  type ActivityScopeSearch,
} from "@/components/activity/ActivityScopeBar";
import {
  MarketCell,
  ModeCurrencyCell,
  OutcomeCell,
  SideCell,
  StateRow,
  Th,
  UserCell,
} from "@/components/activity/ActivityCells";
import { useScopedPage } from "@/components/activity/useScopedPage";
import { useCurrencySearch } from "@/components/activity/useCurrencySearch";
import { CurrencyChip } from "@/components/currency/columns";
import { CurrencyFilter, parseCurrencyFilter } from "@/components/currency/CurrencyFilter";
import type { CurrencyCode } from "@/currency";

/**
 * The shared Event / Market / User scope, plus this page's own currency
 * filter (SM-511).
 *
 * <CurrencyFilter> here and NOT the <StakeCurrencyFilter> the two order
 * lists use, and the difference is the point. Those narrow by the
 * currency an order is DEBITED in, of which there are two. A trade row
 * is money that has already moved, in the leg its own side moved: a
 * CASH fill settles in GOLD either way, a GAME buy debits RED and a
 * GAME sell credits GREEN. All three are reachable, and offering only
 * two would hide every GAME sale on the platform.
 */
export const Route = createFileRoute("/_app/trades")({
  validateSearch: (
    raw: Record<string, unknown>,
  ): ActivityScopeSearch & { currency?: CurrencyCode } => ({
    ...validateActivityScopeSearch(raw),
    currency: parseCurrencyFilter(raw.currency),
  }),
  component: TradeHistoryPage,
});

interface TradeFilters {
  side: string;
  ledger_status: string;
}

// ledger_status is the money leg: a trade is only real cash once the
// debit COMMITTED. PENDING rows that never clear, and FAILED ones, are
// the reason an operator opens this page at all — so they get a filter
// rather than being buried in the column.
const LEDGER_STATUSES = [
  { value: "COMMITTED", label: "Committed" },
  { value: "PENDING", label: "Pending" },
  { value: "FAILED", label: "Failed" },
];

function ledgerTone(s: string): "success" | "warning" | "danger" | "muted" {
  switch (s) {
    case "COMMITTED":
      return "success";
    case "PENDING":
      return "warning";
    case "FAILED":
      return "danger";
    default:
      return "muted";
  }
}

function TradeHistoryPage() {
  const table = useTableState<TradeFilters>({
    defaultSort: { by: "occurred_at", dir: "desc" },
    defaultFilters: { side: "", ledger_status: "" },
  });
  const { scope, setScope, resetKey, hasScope } = useActivityScope();
  const [currency, setCurrency] = useCurrencySearch(parseCurrencyFilter);
  const marketScope = useEventMarketScope(scope);
  // The currency joins the scope's reset key — same reason as every
  // other Activity page: a narrower result set must start at page 1.
  useScopedPage(`${resetKey}|${currency ?? ""}`, table.setPage);

  // The Side filter narrows on the stored BOOK side, which is what
  // settlement_trades indexes. Rows still DISPLAY intent side, so a
  // "Buy NO" row reads BUY while matching the SELL filter — deliberate:
  // filtering on the derived column would mean a table scan per page.
  const params = {
    side: table.filters.side || undefined,
    ledger_status: table.filters.ledger_status || undefined,
    market_id: marketScope.marketIDs,
    user_id: scope.userId || undefined,
    currency,
  };
  const trades = useQuery({
    queryKey: keys.tradeHistoryList(table.limit, table.offset, table.sort, params),
    queryFn: () =>
      fetchTradeHistory({
        limit: table.limit,
        offset: table.offset,
        sort: table.sort,
        ...params,
      }),
    enabled: !marketScope.pending,
  });

  const filtered = hasScope || table.hasActiveFilters || Boolean(currency);
  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-2xl tracking-tight">Trade history</h1>
        <p className="text-sm text-muted">
          Every execution across every user — one row per filled leg, with the
          fee charged and whether the ledger has settled it.
        </p>
      </div>
      <ActivityScopeBar scope={scope} onChange={setScope}>
        <div className="w-40">
          <Select
            label="Ledger"
            placeholder="Any"
            value={table.filters.ledger_status}
            onChange={(e) => table.setFilter("ledger_status", e.target.value)}
            options={LEDGER_STATUSES}
          />
        </div>
        <div className="w-52">
          <CurrencyFilter value={currency} onChange={setCurrency} />
        </div>
        <div className="w-36">
          <Select
            label="Book side"
            placeholder="Any"
            value={table.filters.side}
            onChange={(e) => table.setFilter("side", e.target.value)}
            options={[
              { value: "BUY", label: "Buy" },
              { value: "SELL", label: "Sell" },
            ]}
          />
        </div>
        <ResetScopeButton
          show={filtered}
          onReset={() => {
            table.reset();
            setCurrency(undefined);
            setScope(EMPTY_SCOPE);
          }}
        />
      </ActivityScopeBar>
      <Card>
        <CardHeader className="border-b border-border">
          <CardTitle>Executed trades</CardTitle>
          <span className="text-2xs text-muted">{trades.data?.page.total ?? 0}</span>
        </CardHeader>
        <CardBody className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b border-border bg-surface-2">
                <tr>
                  <Th className="px-5">Market</Th>
                  {/* SM-469: which book and which currency. Never merged
                      into the market column — they are separate facts. */}
                  <Th>Mode · currency</Th>
                  <Th>User</Th>
                  <Th>Outcome</Th>
                  <Th>Side</Th>
                  <Th>Role</Th>
                  <SortableHeader label="Price" field="price" sort={table.sort} onSortChange={table.setSort} align="right" />
                  <SortableHeader label="Qty" field="quantity" sort={table.sort} onSortChange={table.setSort} align="right" />
                  <SortableHeader label="Fee" field="fee_amount" sort={table.sort} onSortChange={table.setSort} align="right" />
                  <Th>Ledger</Th>
                  <SortableHeader label="Executed" field="occurred_at" sort={table.sort} onSortChange={table.setSort} align="right" className="px-5" />
                </tr>
              </thead>
              <tbody>
                {(trades.data?.items ?? []).map((t) => (
                  <tr
                    key={`${t.trade_id}-${t.order_id}`}
                    className="border-b border-border/60 last:border-0"
                  >
                    <MarketCell row={t} />
                    {/* The fill's OWN currency, not the channel's debit
                        leg: a GAME sell settles in GREEN on a book that
                        debits RED, and the chip has to agree with the
                        filter above it. */}
                    <ModeCurrencyCell row={t} currency={t.currency} />
                    <UserCell row={t} />
                    <OutcomeCell row={t} />
                    {/* Intent side/price when the position event was
                        found, book columns otherwise — never a mix, so a
                        row is either fully as-placed or fully as-matched. */}
                    <SideCell side={t.intent_side || t.side} />
                    <td className="px-3 py-2.5 text-2xs text-muted">
                      {t.is_maker ? "MAKER" : "TAKER"}
                    </td>
                    <td className="px-3 py-2.5 text-right font-mono">
                      {/* SM-549: the fill's OWN currency, the same field
                          the chip uses — settlement stamped it from the
                          leg the ledger moved, so nothing is derived. */}
                      <CurrencyAmount
                        currency={t.currency}
                        value={t.intent_price || t.price}
                        trimZeros
                      />
                    </td>
                    <td className="px-3 py-2.5 text-right font-mono">
                      {fmtQty(t.quantity, t.amount_precision)}
                    </td>
                    <td className="px-3 py-2.5 text-right font-mono">
                      <CurrencyAmount currency={t.currency} value={t.fee_amount} />
                    </td>
                    <td className="px-3 py-2.5">
                      <Badge tone={ledgerTone(t.ledger_status)}>
                        {statusLabel(t.ledger_status)}
                      </Badge>
                    </td>
                    <td
                      className="px-5 py-2.5 text-right text-2xs text-muted"
                      title={fmtDate(t.occurred_at)}
                    >
                      {fmtRelative(t.occurred_at)}
                    </td>
                  </tr>
                ))}
                {(trades.isLoading || marketScope.pending) && (
                  <StateRow colSpan={11}>Loading…</StateRow>
                )}
                {trades.isError && (
                  <StateRow colSpan={11}>Could not load trades. Retry in a moment.</StateRow>
                )}
                {trades.data && trades.data.items.length === 0 && (
                  <StateRow colSpan={11}>
                    {/* Name the currency when one is picked — an empty
                        table beside a Currency control otherwise reads
                        as a broken filter rather than an answer. */}
                    {currency ? (
                      <>
                        No trades settled in <CurrencyChip currency={currency} />
                        {hasScope || table.hasActiveFilters ? " for these filters" : ""}.
                      </>
                    ) : (
                      <>No trades{filtered ? " for these filters" : ""}.</>
                    )}
                  </StateRow>
                )}
              </tbody>
            </table>
          </div>
          {trades.data && (
            <Pagination
              page={trades.data.page}
              currentPage={table.page}
              onPageChange={table.setPage}
              itemLabel="trades"
            />
          )}
        </CardBody>
      </Card>
    </div>
  );
}
